/**
 * Capabilities and individual capacity service (plan 074).
 * CRUD for capabilities, individual capabilities, work unit requirements, individual capacity.
 * Eligibility query: users who meet all capability requirements for a work unit.
 */

import { getSupabaseClient } from './supabaseClient';
import { getTenantContext } from './tenantContext';
import type {
  Capability,
  IndividualCapability,
  WorkUnitCapabilityRequirement,
  IndividualCapacity,
} from '../types/capabilities';

const TABLE_CAPABILITIES = 'capabilities';
const TABLE_INDIVIDUAL_CAPABILITIES = 'individual_capabilities';
const TABLE_WU_REQUIREMENTS = 'work_unit_capability_requirements';
const TABLE_INDIVIDUAL_CAPACITY = 'individual_capacity';

// ----------------------------------------------------------------------------
// Capabilities (catalog)
// ----------------------------------------------------------------------------

export async function listCapabilities(workspaceId: string | null): Promise<{
  data: Capability[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const wid = workspaceId ?? getTenantContext()?.workspaceId ?? null;
    if (!wid) return { data: [], error: null };
    const { data, error } = await supabase
      .from(TABLE_CAPABILITIES)
      .select('*')
      .eq('workspace_id', wid)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as Capability[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list capabilities' };
  }
}

export async function createCapability(params: {
  workspace_id: string;
  name: string;
  description?: string | null;
  sort_order?: number;
}): Promise<{ data: Capability | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_CAPABILITIES)
      .insert({
        workspace_id: params.workspace_id,
        name: params.name.trim(),
        description: params.description?.trim() || null,
        sort_order: params.sort_order ?? 0,
      })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as Capability, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to create capability' };
  }
}

export async function updateCapability(
  id: string,
  updates: { name?: string; description?: string | null; sort_order?: number }
): Promise<{ data: Capability | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const payload: Record<string, unknown> = {};
    if (updates.name !== undefined) payload.name = updates.name.trim();
    if (updates.description !== undefined) payload.description = updates.description?.trim() || null;
    if (updates.sort_order !== undefined) payload.sort_order = updates.sort_order;
    const { data, error } = await supabase
      .from(TABLE_CAPABILITIES)
      .update(payload)
      .eq('id', id)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as Capability, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to update capability' };
  }
}

export async function deleteCapability(id: string): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from(TABLE_CAPABILITIES).delete().eq('id', id);
    return { error: error?.message ?? null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to delete capability' };
  }
}

// ----------------------------------------------------------------------------
// Individual capabilities (user + capability + level 1-10)
// ----------------------------------------------------------------------------

export async function listIndividualCapabilitiesByUser(userId: string): Promise<{
  data: IndividualCapability[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_INDIVIDUAL_CAPABILITIES)
      .select('*')
      .eq('user_id', userId)
      .order('capability_id');
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as IndividualCapability[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list individual capabilities' };
  }
}

export async function listIndividualCapabilitiesByWorkspace(workspaceId: string | null): Promise<{
  data: IndividualCapability[];
  error: string | null;
}> {
  try {
    const wid = workspaceId ?? getTenantContext()?.workspaceId ?? null;
    if (!wid) return { data: [], error: null };
    const supabase = getSupabaseClient();
    const { data: caps } = await supabase.from(TABLE_CAPABILITIES).select('id').eq('workspace_id', wid);
    const capIds = (caps ?? []).map((c: { id: string }) => c.id);
    if (capIds.length === 0) return { data: [], error: null };
    const { data, error } = await supabase
      .from(TABLE_INDIVIDUAL_CAPABILITIES)
      .select('*')
      .in('capability_id', capIds)
      .order('user_id')
      .order('capability_id');
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as IndividualCapability[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list individual capabilities' };
  }
}

export async function upsertIndividualCapability(params: {
  user_id: string;
  capability_id: string;
  area_id?: string | null;
  level: number;
  notes?: string | null;
}): Promise<{ data: IndividualCapability | null; error: string | null }> {
  try {
    const level = Math.max(1, Math.min(10, Math.round(params.level)));
    const supabase = getSupabaseClient();
    const row: Record<string, unknown> = {
      user_id: params.user_id,
      capability_id: params.capability_id,
      area_id: params.area_id ?? null,
      level,
      notes: params.notes?.trim() || null,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from(TABLE_INDIVIDUAL_CAPABILITIES)
      .upsert(row, { onConflict: 'user_id,capability_id,area_key' })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as IndividualCapability, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to upsert individual capability' };
  }
}

export async function deleteIndividualCapability(id: string): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from(TABLE_INDIVIDUAL_CAPABILITIES).delete().eq('id', id);
    return { error: error?.message ?? null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to delete individual capability' };
  }
}

// ----------------------------------------------------------------------------
// Work unit capability requirements (gate)
// ----------------------------------------------------------------------------

export async function listWorkUnitCapabilityRequirements(workUnitId: string): Promise<{
  data: WorkUnitCapabilityRequirement[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_WU_REQUIREMENTS)
      .select('*')
      .eq('work_unit_id', workUnitId);
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as WorkUnitCapabilityRequirement[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list requirements' };
  }
}

export async function addWorkUnitCapabilityRequirement(params: {
  work_unit_id: string;
  capability_id: string;
  area_id?: string | null;
  min_level: number;
}): Promise<{ data: WorkUnitCapabilityRequirement | null; error: string | null }> {
  try {
    const min_level = Math.max(1, Math.min(10, Math.round(params.min_level)));
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_WU_REQUIREMENTS)
      .insert({
        work_unit_id: params.work_unit_id,
        capability_id: params.capability_id,
        area_id: params.area_id ?? null,
        min_level,
      })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as WorkUnitCapabilityRequirement, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to add requirement' };
  }
}

export async function removeWorkUnitCapabilityRequirement(id: string): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from(TABLE_WU_REQUIREMENTS).delete().eq('id', id);
    return { error: error?.message ?? null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to remove requirement' };
  }
}

/**
 * Users who have all required capabilities at >= min_level for this work unit.
 * When a requirement has area_id, only individual capabilities with that area (or general/null) count.
 * Returns user ids; empty if no requirements or no one meets them.
 */
export async function getEligibleUserIdsForWorkUnit(workUnitId: string): Promise<{
  data: string[];
  error: string | null;
}> {
  try {
    const { data: reqs, error: reqErr } = await listWorkUnitCapabilityRequirements(workUnitId);
    if (reqErr || !reqs.length) return { data: [], error: reqErr ?? null };
    const supabase = getSupabaseClient();
    let eligibleIds: Set<string> | null = null;
    for (const r of reqs) {
      let q = supabase
        .from(TABLE_INDIVIDUAL_CAPABILITIES)
        .select('user_id')
        .eq('capability_id', r.capability_id)
        .gte('level', r.min_level);
      if (r.area_id != null) {
        q = q.or(`area_id.eq.${r.area_id},area_id.is.null`);
      }
      const { data: ind, error: indErr } = await q;
      if (indErr) return { data: [], error: indErr.message };
      const userIds = new Set((ind ?? []).map((x: { user_id: string }) => x.user_id));
      if (eligibleIds === null) eligibleIds = userIds;
      else eligibleIds = new Set([...eligibleIds].filter((id) => userIds.has(id)));
    }
    return { data: eligibleIds ? [...eligibleIds] : [], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to get eligible users' };
  }
}

// ----------------------------------------------------------------------------
// Individual capacity (pool: available hours per person)
// ----------------------------------------------------------------------------

export async function listIndividualCapacity(workspaceId: string | null): Promise<{
  data: IndividualCapacity[];
  error: string | null;
}> {
  try {
    const wid = workspaceId ?? getTenantContext()?.workspaceId ?? null;
    if (!wid) return { data: [], error: null };
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_INDIVIDUAL_CAPACITY)
      .select('*')
      .eq('workspace_id', wid);
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as IndividualCapacity[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list individual capacity' };
  }
}

export async function upsertIndividualCapacity(params: {
  user_id: string;
  workspace_id: string;
  available_hours_per_week: number;
  effective_from?: string | null;
  effective_to?: string | null;
}): Promise<{ data: IndividualCapacity | null; error: string | null }> {
  try {
    const hours = Math.max(0, Number(params.available_hours_per_week));
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_INDIVIDUAL_CAPACITY)
      .upsert(
        {
          user_id: params.user_id,
          workspace_id: params.workspace_id,
          available_hours_per_week: hours,
          effective_from: params.effective_from || null,
          effective_to: params.effective_to || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,workspace_id' }
      )
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as IndividualCapacity, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to upsert individual capacity' };
  }
}

export async function deleteIndividualCapacity(id: string): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from(TABLE_INDIVIDUAL_CAPACITY).delete().eq('id', id);
    return { error: error?.message ?? null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to delete individual capacity' };
  }
}

/**
 * Summary: total people with capacity and total available hours (for pool display).
 */
export async function getCapacityPoolSummary(workspaceId: string | null): Promise<{
  data: { personCount: number; totalHoursPerWeek: number } | null;
  error: string | null;
}> {
  try {
    const { data: list, error } = await listIndividualCapacity(workspaceId);
    if (error) return { data: null, error };
    const personCount = list.length;
    const totalHoursPerWeek = list.reduce((sum, c) => sum + Number(c.available_hours_per_week), 0);
    return { data: { personCount, totalHoursPerWeek }, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to get pool summary' };
  }
}
